import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { FinancialTransaction } from "../models/FinancialTransaction";
import { FinancialTransactionService } from "../services/FinancialTransactionService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface FinancialTransactionContextProps {
  loading: Loadings;
  error: Errors;
  financialTransaction: FinancialTransaction | null;
  financialTransactions: FinancialTransaction[];
  updateFinancialTransaction: (
    financialTransactionId: string,
    financialTransactionData: Partial<
      Omit<FinancialTransaction, "_id" | "createdAt" | "updatedAt">
    >,
  ) => Promise<void>;
  deleteFinancialTransactionById: (
    financialTransactionId: string,
  ) => Promise<void>;
  setSelectedFinancialTransaction: (financialTransactionId: string) => void;
}

const FinancialTransactionContext = createContext<
  FinancialTransactionContextProps | undefined
>(undefined);

export const useFinancialTransaction = (): FinancialTransactionContextProps => {
  const context = useContext(FinancialTransactionContext);
  if (!context) {
    throw new Error(
      "useFinancialTransaction must be used within a FinancialTransactionProvider",
    );
  }
  return context;
};

export const FinancialTransactionProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [financialTransactions, setFinancialTransactions] = useState<
    FinancialTransaction[]
  >([]);
  const [financialTransaction, setFinancialTransaction] =
    useState<FinancialTransaction | null>(null);
  const [loading, setLoading] = useState<Loadings>({});
  const [error, setError] = useState<Errors>({});

  const fetchFinancialTransactions = async () => {
    if (!user || !currentMatterId) return;
    try {
      const docs = await FinancialTransactionService.getFinancialTransactions();
      setFinancialTransactions(docs);
      setError((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setError((prev) => ({ ...prev, list: err }));
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    setLoading((prev) => ({ ...prev, list: true }));
    fetchFinancialTransactions();
  }, [user, currentMatterId]);

  useSocketEventByIds(
    "financialtransactions:changed",
    ["*"],
    (updatedDocument: FinancialTransaction) => {
      fetchFinancialTransactions();
    },
  );

  const setSelectedFinancialTransaction = async (
    financialTransactionId: string,
  ) => {
    const fetchedFinancialTransaction =
      await FinancialTransactionService.getFinancialTransaction(
        financialTransactionId,
      );
    setFinancialTransaction(fetchedFinancialTransaction);
  };

  const updateFinancialTransaction = async (
    financialTransactionId: string,
    updatedData: Partial<
      Omit<FinancialTransaction, "_id" | "createdAt" | "updatedAt">
    >,
  ) => {
    await FinancialTransactionService.updateFinancialTransaction(
      financialTransactionId,
      {
        ...updatedData,
      },
    );
  };

  const deleteFinancialTransactionById = async (
    financialTransactionId: string,
  ) => {
    if (!currentMatterId) return;
    await FinancialTransactionService.deleteFinancialTransaction(
      currentMatterId,
      financialTransactionId,
    );
    fetchFinancialTransactions();
  };

  return (
    <FinancialTransactionContext.Provider
      value={{
        loading,
        error,
        financialTransaction,
        financialTransactions,
        updateFinancialTransaction,
        deleteFinancialTransactionById,
        setSelectedFinancialTransaction,
      }}
    >
      {children}
    </FinancialTransactionContext.Provider>
  );
};
